<template>
    <div class="flex mt-[42px] mb-[27px] justify-between h-[42px]">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.my_profile") }}
        </h1>
    </div>

    <SettingsTabs class="mt-[24px]" tab-key="security-settings">
        <div class="mb-[98px] mt-[25px]">
            <div class="mt-[42px] grid-cols-layout pb-5 lg:grid">
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                    >
                        {{ $t("vue.password") }}
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        {{ $t("vue.change_password_here") }}
                    </p>
                </div>

                <form
                    class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0"
                    @submit.prevent="updatePassword"
                >
                    <div class="flex flex-col border-b border-gray-200 pl-6">
                        <TextInput
                            v-model.trim="state.form.password.current_password"
                            class="mt-5 w-full pr-6 sm:w-80 sm:pr-0"
                            :label="$t('vue.current_password')"
                            type="password"
                            :placeholder="$t('vue.enter_current_password')"
                            :error="getPasswordError('current_password')"
                            @change="v$.form.password.current_password.$touch()"
                        />

                        <TextInput
                            v-model.trim="state.form.password.password"
                            class="mt-6 w-full pr-6 sm:w-80 sm:pr-0"
                            :label="$t('vue.new_password')"
                            type="password"
                            :placeholder="$t('vue.enter_new_password')"
                            :error="getPasswordError('password')"
                            @change="v$.form.password.password.$touch()"
                        />
                        <TextInput
                            v-model.trim="
                                state.form.password.password_confirmation
                            "
                            class="my-6 w-full pr-6 sm:w-80 sm:pr-0"
                            :label="$t('vue.confirm_new_password')"
                            type="password"
                            :placeholder="$t('vue.enter_new_password_again')"
                            :error="getPasswordError('password_confirmation')"
                            @change="
                                v$.form.password.password_confirmation.$touch()
                            "
                        />
                    </div>
                    <div class="flex justify-end">
                        <div class="my-3 pr-6">
                            <AtomicButton
                                type="submit"
                                color="yellow"
                                size="sm"
                                :disabled="v$.form.password.$invalid || loading"
                            >
                                {{ $t("vue.update_password") }}
                            </AtomicButton>
                        </div>
                    </div>
                </form>
            </div>

            <TwoFactorAuthentication />
        </div>
    </SettingsTabs>
</template>

<script lang="ts" setup>
import useVuelidate from "@vuelidate/core";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";
import { usePage } from "@inertiajs/vue3";
import { useTranslations } from "@/composables/useTranslations";

import {
    required,
    email,
    sameAs,
    minLength,
    helpers,
    maxLength,
} from "@vuelidate/validators";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import type IInputErrors from "@/types/Atomic/IInputErrors";

const { t } = useTranslations();
const globalProps = usePage<IRhizomPageProps>().props;
const imgUrl = ref("");
const img = ref<File | null>(null);
const fileProfilePhoto = ref<HTMLElement | null>(null);
const errors = ref<IInputErrors>({});
const loading = ref(false);
const { fetchTeams } = useTeamsAndMembers();

const state = reactive({
    form: {
        profileInfo: {
            name: "",
            email: "",
        },
        password: {
            current_password: "",
            password: "",
            password_confirmation: "",
        },
    },
});

const passwordRef = computed(() => state.form.password.password);

const rules = {
    form: {
        profileInfo: {
            name: {
                required,
                maxLength: maxLength(255),
            },
            email: {
                required,
                email,
                maxLength: maxLength(255),
            },
        },
        password: {
            current_password: {},
            password: {
                required,
                minLength: minLength(8),
            },
            password_confirmation: {
                required,
                sameAsPasswordNew: helpers.withMessage(
                    t("vue.passwords_do_not_match"),
                    sameAs(passwordRef),
                ),
            },
        },
    },
};
const v$ = useVuelidate(rules, state);

onMounted(() => {
    state.form.profileInfo.name = globalProps.user.name;
    state.form.profileInfo.email = globalProps.user.email;
    if (globalProps.user.profile_photo_url) {
        imgUrl.value = globalProps.user.profile_photo_url;
    }
});

const updateNameAndEmail = () => {
    v$.value.form.profileInfo.$touch();
    if (v$.value.form.profileInfo.$error) {
        () => undefined;
    } else {
        loading.value = true;

        router.put(
            route("settings.profile-settings.update"),
            state.form.profileInfo,
            {
                preserveScroll: true,
                onSuccess: () => {
                    errors.value = {};
                    create({
                        title: "Profile updated",
                        type: "success",
                    });
                    v$.value.form.profileInfo.$reset();
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: "Profile not updated",
                        text: "There was an error updating your profile.",
                        type: "error",
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            },
        );
    }
};

const addPhoto = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const files = target.files as FileList;
    img.value = files[0];
    imgUrl.value = URL.createObjectURL(img.value);
    let formData = new FormData();
    formData.append("photo", img.value);
    formData.append("_method", "put");
    router.post(route("settings.profile-settings.update"), formData, {
        preserveScroll: true,
        onSuccess: () => {
            create({
                title: "Profile photo updated",
                type: "success",
            });
            fetchTeams();
        },
        onError: () => {
            create({
                title: "Photo not updated",
                text: "There was an error updating your photo.",
                type: "error",
            });
        },
    });
};

const removePhoto = () => {
    const input = document.querySelector(
        "#profileImageUpload",
    ) as HTMLInputElement;

    if (input !== null) {
        input.value = "";
    }

    img.value = null;
    imgUrl.value = "";

    router.delete(route("settings.profile-settings.destroy"), {
        preserveScroll: true,
        onSuccess: () => {
            create({
                title: "Profile photo updated",
                type: "success",
            });
            fetchTeams();
        },
        onError: () => {
            create({
                title: "Photo not updated",
                text: "There was an error updating your photo.",
                type: "error",
            });
        },
    });
};

const getProfileError = (field: string) => {
    return (
        v$.value.form.profileInfo[field]?.$errors[0]?.$message ||
        errors.value?.updateProfileInformation?.[field] ||
        ""
    );
};

const getPasswordError = (field: string) => {
    return (
        v$.value.form.password[field]?.$errors[0]?.$message ||
        errors.value?.updatePassword?.[field] ||
        ""
    );
};

const updatePassword = () => {
    v$.value.form.password.$touch();
    if (v$.value.form.password.$error) {
        () => undefined;
    } else {
        loading.value = true;

        router.put(
            route("settings.profile-settings.update"),
            state.form.password,
            {
                preserveScroll: true,
                onSuccess: () => {
                    ((state.form.password = {
                        current_password: "",
                        password: "",
                        password_confirmation: "",
                    }),
                        v$.value.form.password.$reset());

                    create({
                        title: "Password updated",
                        text: "Your password has been successfully updated.",
                        type: "success",
                    });
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: "Password not updated",
                        text: "There was an error updating your password.",
                        type: "error",
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            },
        );
    }
};
const clickToUpload = () => {
    const element = fileProfilePhoto.value as HTMLInputElement;
    element.click();
};
</script>

<style></style>
